import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI } from '../../lib/api';
import { Loader2, Activity, ClipboardList, FileText, DollarSign, FolderOpen, CalendarDays, Shuffle, CheckCircle2, FilePlus2, Receipt, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const EVENT_ICONS = {
  project_created: Shuffle,
  project_updated: Activity,
  converted_to_project: Shuffle,
  status_changed: Activity,
  task_created: ClipboardList,
  task_updated: ClipboardList,
  task_deleted: ClipboardList,
  budget_group_added: DollarSign,
  budget_group_deleted: DollarSign,
  budget_item_added: DollarSign,
  budget_item_deleted: DollarSign,
  budget_updated: DollarSign,
  change_order_created: FilePlus2,
  change_order_approved: FilePlus2,
  change_order_rejected: FilePlus2,
  change_order_deleted: FilePlus2,
  invoice_created: Receipt,
  invoice_updated: Receipt,
  invoice_deleted: Receipt,
  payment_received: CreditCard,
  payment_deleted: CreditCard,
  daily_log_added: CalendarDays,
  daily_log_deleted: CalendarDays,
  file_uploaded: FolderOpen,
  file_deleted: FolderOpen,
};

function relativeTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export function ProjectActivity({ projectId }) {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await projectsAPI.getActivity(projectId, { limit: 100 });
      setActivity(res.data.activity || []);
    } catch {
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  if (activity.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="activity-empty">
        <Activity className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-lg font-medium">No activity yet</p>
        <p className="text-sm mt-1">Activity will appear here as the project progresses.</p>
      </div>
    );
  }

  // Group by date
  const grouped = {};
  activity.forEach(a => {
    const dateKey = a.created_at ? new Date(a.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'Unknown';
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(a);
  });

  return (
    <div className="space-y-6" data-testid="project-activity">
      {Object.entries(grouped).map(([dateLabel, events]) => (
        <div key={dateLabel}>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{dateLabel}</h3>
          <div className="space-y-0">
            {events.map((event, i) => {
              const Icon = EVENT_ICONS[event.event_type] || Activity;
              return (
                <div key={event.id} className="flex gap-3 pb-4" data-testid={`activity-event-${event.id}`}>
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-muted-foreground" />
                    </div>
                    {i < events.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <p className="text-sm">{event.description}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      {event.actor_name && <span>{event.actor_name}</span>}
                      <span>{relativeTime(event.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
